/**
 * Icon — the corpus's line-glyph set, reached by name so no route pastes a path.
 *
 * Corpus: `DB:1852-1863` (eight sidebar glyphs, 15×15,
 * stroke-width 1.9, `aria-hidden` set on the element itself),
 * `SI:254-262` (five role glyphs, 20×20, 1.7),
 * `PR1:559-569` (four feature glyphs, 20×20, 1.8),
 * `PR1:532-533` (chevron, 16×16, 2.2, `ghNudge`),
 * `DB:1885-1886` (bell, 19×19, 1.8) and
 * `DB:1938-1939` with `CC:70-71` (photo, 18×18).
 * The characters are `DB:254` (`+`), `:2702` (`–`), `:2754` (`✓`),
 * `:2792` (`←`/`→`), `:91` (`✕`) and `PR1:236` (`↗`).
 *
 * Every shape is one `<path>` in a 24-unit box, `fill: none`,
 * `stroke: currentColor`, round caps and joins — so colour belongs to the parent
 * and is never a prop. `SI:262` recolours the selected role
 * glyph green, which under `currentColor` is the caller's own `color`; that is
 * why there is no `selected` state here.
 *
 * Four disclosures rather than four smoothings:
 *  - `jobs` (DB:1859) and the role picker's `trade` (SI:259) are byte-identical
 *    spanners, so the union carries one name. A role picker maps `trade` to
 *    `'jobs'`; two names for one path would silently double the set.
 *  - `properties` (DB:1858) and `tenant` (SI:255) are NOT one glyph. They share
 *    the house outline and `tenant` adds a door, `M10 19v-5h4v5`. Both are kept.
 *  - `tokens.css` has no icon-size scale and no stroke ramp, so 15/16/20px and
 *    1.7/1.9/2.2 sit raw in `icon.css`. That is the debt `button.css`'s
 *    `padding: 10px 18px` already carries, not an exemption from
 *    it. The bell's 19px and the photo's 18px have no size name at all and round
 *    to `md`; `ghNudge`'s 1.8s has no duration token and takes the nearest,
 *    `--rp-duration-shimmer` (1.4s).
 *  - `evIcon` (DB:1939) and `thumbIcon` (CC:71) draw the photo glyph with round
 *    joins but no `strokeLinecap`, the one place the corpus departs from round
 *    caps. One attribute across the set is worth more than the departure.
 *
 * `title` is the only string a person reads and it arrives as a prop.
 */

/** Glyphs the corpus draws as a path in a 24-unit box. */
const PATHS = {
  overview: 'M4 13h6V4H4z M4 20h6v-4H4z M14 20h6v-9h-6z M14 8h6V4h-6z',
  cases: 'M5 4h10l4 4v12H5z M14 4v5h5',
  practice: 'M3 21h18 M5 21V7l7-4 7 4v14 M9 21v-6h6v6',
  properties: 'M4 11l8-7 8 7 M6 10v9h12v-9',
  jobs: 'M14.5 6.5a4 4 0 0 0-5.7 4.6L4 16v4h4l4.9-4.8a4 4 0 0 0 4.6-5.7l-2.8 2.8-2.5-2.5 2.3-2.3z',
  messages: 'M4 5h16v11H8l-4 4z',
  people:
    'M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6 M16 4.6a3.5 3.5 0 0 1 0 6.8 M17 14.5c2.3.7 4 2.9 4 5.5',
  settings:
    'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M19 12a7 7 0 0 0-.1-1.2l2-1.5-2-3.4-2.3 1a7 7 0 0 0-2-1.2L14.2 3h-4L9.6 5.7a7 7 0 0 0-2 1.2l-2.3-1-2 3.4 2 1.5A7 7 0 0 0 5 12',
  tenant: 'M4 11l8-7 8 7 M6 10v9h12v-9 M10 19v-5h4v5',
  advocate: 'M12 21s-7-4.4-7-10V6l7-3 7 3v5c0 5.6-7 10-7 10z',
  manager: 'M4 8h16v11H4z M9 8V6h6v2 M4 13h16 M11 13v2h2v-2',
  owner: 'M11.5 8a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0z M11 10l8 8 M16 15l2-2 M13 18l2-2',
  camera: 'M4 8h3l2-3h6l2 3h3v11H4z M12 17a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z',
  clock: 'M12 21a8 8 0 1 0 0-16 8 8 0 0 0 0 16z M12 9v4l3 2 M12 3v2 M9 3h6',
  mail: 'M4 6h16v12H4z M4 7l8 6 8-6',
  'arrow-out': 'M7 17 17 7 M9 7h8v8',
  'chevron-down': 'M5 9l7 7 7-7',
  bell: 'M6 9a6 6 0 1 1 12 0c0 5 2 6 2 6H4s2-1 2-6 M10 19a2 2 0 0 0 4 0',
  photo: 'M4 17l5-6 4 4 3-3 4 5 M4 5h16v14H4z',
} as const;

/**
 * Glyphs the corpus draws as characters, not paths. They are named here so a
 * caller stops hard-coding them — a bare `✕` pasted into a button is exactly the
 * case where the accessible name goes missing (`DB:91` supplies
 * one; the character itself carries nothing). `minus` is U+2013, the en dash the
 * expander at `DB:2702` actually uses, not a hyphen.
 *
 * These are marks and not copy. They are the character form of the path data
 * above, no reader is ever meant to hear them as words, and the one string a
 * person actually reads is still `title` — which arrives as a prop.
 */
const CHARACTERS = {
  plus: '+',
  minus: '–',
  tick: '✓',
  close: '✕',
  external: '↗',
  'arrow-forward': '→',
  'arrow-back': '←',
} as const;

/**
 * The glyphs the corpus mirrors, and the character each one mirrors to.
 * `DB:2792` writes `dir === 'rtl' ? '→' : '←'` — it swaps the
 * character rather than transforming the mark, because `scaleX(-1)` on an
 * asymmetric glyph gives a reversed arrowhead on a stem that did not move.
 * `external` (`↗`) is the same shape of mark — a directional stroke with a
 * head — so it mirrors the same way, to `↖`; a link glyph pointing away from
 * the reading direction in an RTL interface points at the wrong corner
 * otherwise. Nothing else flips: a house, a spanner and a bell read the same
 * in Arabic.
 *
 * Keyed by name and valued by the mirrored character directly, rather than by
 * a second `IconName`, so a mirror target never has to be a selectable glyph
 * in its own right — `↖` names nothing a caller would ever ask `Icon` for.
 */
const MIRRORED: Partial<Record<keyof typeof CHARACTERS, string>> = {
  'arrow-forward': CHARACTERS['arrow-back'],
  'arrow-back': CHARACTERS['arrow-forward'],
  external: '↖',
};

export type IconName = keyof typeof PATHS | keyof typeof CHARACTERS;
export type IconSize = 'sm' | 'nav' | 'md';

/**
 * Every name, at runtime. The union above is erased before any test can read it,
 * so a glyph added to `PATHS` and forgotten in the story fixtures would be drawn
 * by no story, audited by no axe run and seen by no reviewer — the same omission
 * blindness `catalogue.md` exists to fix, one level down. `icon.test.tsx`
 * compares this list with the fixtures, so forgetting one fails the build.
 */
export const iconNames: readonly IconName[] = [
  ...(Object.keys(PATHS) as (keyof typeof PATHS)[]),
  ...(Object.keys(CHARACTERS) as (keyof typeof CHARACTERS)[]),
];

/**
 * PR1:533, DB:1853 and SI:262 — the corpus draws one line-art system at four
 * weights, and the weight tracks the size rather than the meaning. Making it a
 * per-size default is what turns five copies of that system into one component;
 * the fourth weight, the feature set's 1.8, arrives through the prop.
 */
const STROKE_FOR_SIZE: Record<IconSize, number> = { sm: 2.2, nav: 1.9, md: 1.7 };

const isCharacter = (name: IconName): name is keyof typeof CHARACTERS => name in CHARACTERS;

export interface IconProps {
  readonly name: IconName;
  readonly size?: IconSize;
  /**
   * Overrides the per-size default: the feature set is 1.8 and the photo 1.6.
   * A typed character has no stroke, so this does nothing to one.
   */
  readonly strokeWidth?: number;
  /**
   * Accessible name. Absent — which is what every corpus instance does — the
   * glyph is decorative and hidden, because in this library colour and shape
   * reinforce text rather than replace it. Supplied, the glyph becomes an image
   * with a name, and a caller reaching for that to avoid writing a label is
   * doing the thing the rule exists to stop.
   *
   * Blank or whitespace-only counts as absent, so a name that has not loaded
   * yet leaves a hidden glyph rather than a nameless image.
   */
  readonly title?: string;
  readonly animate?: 'none' | 'nudge';
  /** Mirrors `arrow-forward` / `arrow-back` in RTL. No other name is affected. */
  readonly flipForRtl?: boolean;
  readonly className?: string;
}

export function Icon({
  name,
  size = 'md',
  strokeWidth,
  title,
  animate = 'none',
  flipForRtl = false,
  className,
}: IconProps) {
  const classes = [
    'rp-icon',
    `rp-icon--${size}`,
    animate === 'nudge' ? 'rp-icon--nudge' : undefined,
    className,
  ]
    .filter(Boolean)
    .join(' ');
  // `role="img"` with a name, or hidden outright. There is no third option: an
  // unlabelled `<svg>` with no `aria-hidden` is announced as "graphic" by some
  // screen readers and as nothing by others, which is a coin toss the reader
  // loses either way.
  //
  // Blank counts as absent. `title=""` is supplied to `!== undefined` and absent
  // to a reader, so trusting the prop's presence would build the very thing the
  // paragraph above refuses — a role with no name, which axe scores `svg-img-alt`
  // serious. It is not hypothetical: `title={label}` is blank on every render
  // before the label arrives.
  const named = title !== undefined && title.trim() !== '';
  const described = named ? { role: 'img' } : { 'aria-hidden': true as const };

  if (isCharacter(name)) {
    // Both mirrors are in the DOM and the stylesheet shows one, because
    // direction is a rendered fact this component cannot read at build time and
    // will not guess. Neither is announced: the wrapper carries the name, and a
    // reader hearing "back arrow forward arrow" would be worse off than one
    // hearing nothing.
    const mirror = flipForRtl ? MIRRORED[name] : undefined;
    return (
      <span {...described} aria-label={named ? title : undefined} className={classes}>
        <span aria-hidden="true" className={mirror ? 'rp-icon__mirror-ltr' : undefined}>
          {CHARACTERS[name]}
        </span>
        {mirror ? (
          <span aria-hidden="true" className="rp-icon__mirror-rtl">
            {mirror}
          </span>
        ) : undefined}
      </span>
    );
  }

  return (
    <svg {...described} className={classes} viewBox="0 0 24 24" focusable="false">
      {named ? <title>{title}</title> : undefined}
      <path
        d={PATHS[name]}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth ?? STROKE_FOR_SIZE[size]}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
